import React, { useState, useEffect, useMemo } from 'react';
import { RefreshCw, Database, Edit3, Check, X, Save, Download } from 'lucide-react';
import * as XLSX from 'xlsx'; // 📥 엑셀 라이브러리 추가
// API 통합관리
import { API_BASE_URL } from '../../config';

interface ProfitData {
  forecast_id: string;
  month: number;
  client_name: string;
  team_name: string;
  manager_name: string;
  contract_product: number;
  contract_goods: number;
  purchase_cost: number;
  labor_cost: number;
}

interface ProfitAnalysisProps {
  globalRefreshKey?: number; // 🚨 [마지막 추가] 실시간 신호탄
}

// const API_BASE_URL = `http://${window.location.hostname}:8000`;



export default function ProfitAnalysis({ globalRefreshKey = 0 }: ProfitAnalysisProps) {
  const [data, setData] = useState<ProfitData[]>([]);
  const [year, setYear] = useState(new Date().getFullYear());
  const [availableYears, setAvailableYears] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);
  
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValues, setEditValues] = useState({ purchase_cost: 0, labor_cost: 0 });

  const fetchAvailableYears = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/profit/years`);
      const years = await res.json();
      if (Array.isArray(years) && years.length > 0) {
        setAvailableYears(years);
        if (!years.includes(year)) setYear(years[0]);
      }
    } catch (err) { console.error("연도 목록 로드 실패:", err); }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/profit/analysis?year=${year}`);
      const json = await res.json();
      setData(Array.isArray(json) ? json : []);
    } catch (err) { setData([]); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchAvailableYears(); }, [globalRefreshKey]); //  globalRefreshKey 추가로 외부에서 강제 새로고침 가능하도록
  useEffect(() => { fetchData(); }, [year, globalRefreshKey]); // year 변경 시와 globalRefreshKey 변경 시 모두 데이터 새로고침

  const groupedData = useMemo(() => {
    const groups: { [key: string]: ProfitData[] } = {};
    data.forEach(item => {
      if (!groups[item.manager_name]) groups[item.manager_name] = [];
      groups[item.manager_name].push(item);
    });
    return groups;
  }, [data]);

  const startEdit = (item: ProfitData) => {
    setEditingId(item.forecast_id);
    setEditValues({ purchase_cost: item.purchase_cost, labor_cost: item.labor_cost });
  };

  const saveEdit = async () => {
    if (!editingId) return;
    setData(prev => prev.map(item => item.forecast_id === editingId ? { ...item, ...editValues } : item));
    try {
        await fetch(`${API_BASE_URL}/profit/cost/${editingId}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(editValues)
        });
    } catch (err) { alert("저장 중 오류 발생"); fetchData(); }
    setEditingId(null);
  };

  const handleMigration = async () => {
    const isConfirmed = window.confirm(`⚠️ [${year}년 데이터 동기화 경고]\n\n기존 원가가 덮어씌워질 수 있습니다. 진행하시겠습니까?`);
    if (!isConfirmed) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/profit/migrate/${year}`, { method: 'POST' });
      const result = await res.json();
      if (res.ok) { alert(result.message); fetchData(); } 
      else { alert("실패: " + (result.detail || result.message)); }
    } catch (err) { alert("서버 오류"); }
    finally { setLoading(false); }
  };

  // =================================================================
  // 📥 [강력한 기능] UI 틀을 유지한 엑셀 다운로드 (xlsx 활용)
  // =================================================================
  const downloadExcel = () => {
    if (data.length === 0) return alert("데이터가 없습니다.");
    
    const isConfirmed = window.confirm(`📂 [${year}년도 수익률 분석표]를 엑셀로 저장하시겠습니까?`);
    if (!isConfirmed) return;

    // 1. 데이터 가공 (UI 순서와 동일하게)
    const excelRows: any[] = [];
    
    // 헤더 정의 (2줄 구조를 위해 수동 구성)
    // 첫 번째 줄: 대분류
    excelRows.push(["", "", "", "", "", "계약금액 (Forecast)", "", "", "", "수익률 분석 (Actual)", "", "", ""]);
    // 두 번째 줄: 소분류
    excelRows.push(["월", "거래처", "팀", "담당자", "제품매출", "상품매출", "매출소계", "제품%", "상품%", "매입원가", "수익금액", "수익률(%)", "인건비", "최종수익"]);

    // 데이터 넣기
    Object.entries(groupedData).forEach(([manager, items]) => {
      items.forEach(row => {
        const total = row.contract_product + row.contract_goods;
        const profit = total - row.purchase_cost;
        const rate = total ? ((profit / total) * 100).toFixed(1) : "0";
        const final = profit - row.labor_cost;
        
        excelRows.push([
          `${row.month}월`, row.client_name, row.team_name, row.manager_name,
          row.contract_product, row.contract_goods, total,
          total ? (row.contract_product/total*100).toFixed(0)+'%' : '0%',
          total ? (row.contract_goods/total*100).toFixed(0)+'%' : '0%',
          row.purchase_cost, profit, `${rate}%`, row.labor_cost, final
        ]);
      });

      // 담당자 요약행 추가 (선택 사항)
      const subTotal = items.reduce((s, i) => s + (i.contract_product + i.contract_goods), 0);
      const subPur = items.reduce((s, i) => s + i.purchase_cost, 0);
      excelRows.push([`${manager} 합계`, "", "", "", "", "", subTotal, "", "", subPur, subTotal - subPur, "", "", ""]);
    });

    // 2. 워크시트 생성
    const worksheet = XLSX.utils.aoa_to_sheet(excelRows);

    // 3. 셀 병합 설정 (Merge)
    // s: start, r: row, c: col / e: end
    worksheet['!merges'] = [
      { s: { r: 0, c: 5 }, e: { r: 0, c: 8 } },  // 계약금액 병합
      { s: { r: 0, c: 9 }, e: { r: 0, c: 13 } }  // 수익률분석 병합
    ];

    // 4. 파일 생성 및 저장
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "수익분석");
    XLSX.writeFile(workbook, `${year}년_영업본부_수익률분석표.xlsx`);
  };

  return (
    <div className="p-4 h-full flex flex-col bg-slate-50 font-sans text-[11px]">
      <div className="flex justify-between items-center mb-4 bg-white p-3 rounded-lg shadow-sm border border-slate-200">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-600 rounded-lg">
            <Save className="text-white" size={20} />
          </div>
          <h2 className="text-xl font-black text-slate-800 tracking-tighter">
            {year}년 영업본부 수익률 분석 <span className="text-blue-600"></span>
          </h2>
        </div>
        
        <div className="flex gap-2">
          <button onClick={handleMigration} className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-500 text-white rounded-md font-bold hover:bg-amber-600 transition shadow-sm">
            <Database size={14} /> {year}년 동기화
          </button>

          {/* 📥 엑셀 저장 버튼 */}
          <button onClick={downloadExcel} className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white rounded-md font-bold hover:bg-green-700 transition shadow-sm">
            <Download size={14} /> Excel 저장
          </button>

          <select value={year} onChange={(e) => setYear(Number(e.target.value))} className="p-1.5 border border-slate-300 rounded-md font-bold bg-white outline-none focus:ring-2 focus:ring-blue-500">
            {availableYears.map(y => <option key={y} value={y}>{y}년</option>)}
          </select>
          
          <button onClick={fetchData} className="p-1.5 bg-white border border-slate-300 rounded-md hover:bg-slate-100 transition shadow-sm">
            <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
          </button>
        </div>
      </div>

      {/* 테이블 영역 (기존 UI 유지) */}
      <div className="flex-1 overflow-auto border border-slate-300 shadow-xl rounded-lg bg-white">
        <table className="min-w-full text-center -> border-separate border-spacing-0">
          <thead className="bg-slate-800 text-white sticky top-0 z-30">
            <tr>
              <th rowSpan={3} className="border border-slate-700 p-2 w-[40px]">NO.</th>
              <th rowSpan={3} className="border border-slate-700 p-2 w-[50px]">월</th>
              <th rowSpan={3} className="border border-slate-700 p-2 min-w-[180px]">거래처</th>
              <th rowSpan={3} className="border border-slate-700 p-2">팀</th>
              <th rowSpan={3} className="border border-slate-700 p-2">담당자</th>
              <th colSpan={5} className="border border-slate-700 p-1 bg-slate-700 font-bold">계약금액 (Forecast)</th>
              <th colSpan={5} className="border border-slate-700 p-1 bg-blue-900 font-bold">수익률 분석 (Actual)</th>
              <th rowSpan={3} className="border border-slate-700 p-2 w-[60px]">수정</th>
            </tr>
            <tr className="text-[10px]">
              <th rowSpan={2} className="border border-slate-700 p-1">제 품</th>
              <th rowSpan={2} className="border border-slate-700 p-1">상 품</th>
              <th rowSpan={2} className="border border-slate-700 p-1 font-bold text-amber-400">소 계</th>
              <th colSpan={2} className="border border-slate-700 p-1">비율</th>
              <th colSpan={2} className="border border-slate-700 p-1 bg-blue-800 font-bold">소 계</th>
              <th rowSpan={2} className="border border-slate-700 p-1 bg-blue-800">수익율</th>
              <th rowSpan={2} className="border border-slate-700 p-1 bg-blue-800 text-red-400">인건비</th>
              <th rowSpan={2} className="border border-slate-700 p-1 bg-blue-800 text-green-400">최종수익</th>
            </tr>
            <tr className="text-[9px]">
              <th className="border border-slate-700 p-1">제품%</th>
              <th className="border border-slate-700 p-1">상품%</th>
              <th className="border border-slate-700 p-1 bg-blue-800 text-red-300">매입금액</th>
              <th className="border border-slate-700 p-1 bg-blue-800 text-slate-100">수익금액</th>
            </tr>
          </thead>

          <tbody className="divide-y divide-slate-200">
            {Object.entries(groupedData).map(([manager, items]) => {
              const sub_product = items.reduce((s, i) => s + i.contract_product, 0);
              const sub_goods = items.reduce((s, i) => s + i.contract_goods, 0);
              const sub_total_sales = sub_product + sub_goods;
              const sub_purchase = items.reduce((s, i) => s + i.purchase_cost, 0);
              const sub_labor = items.reduce((s, i) => s + i.labor_cost, 0);
              const sub_profit_amt = sub_total_sales - sub_purchase;
              const sub_final_profit = sub_profit_amt - sub_labor;
              const sub_profit_rate = sub_total_sales ? (sub_profit_amt / sub_total_sales * 100) : 0;

              return (
                <React.Fragment key={manager}>
                  {items.map((row, idx) => {
                    const isEditing = editingId === row.forecast_id;
                    const contract_total = row.contract_product + row.contract_goods;
                    const current_purchase = isEditing ? editValues.purchase_cost : row.purchase_cost;
                    const profit_amt = contract_total - current_purchase;
                    const profit_rate = contract_total ? (profit_amt / contract_total * 100) : 0;
                    const current_labor = isEditing ? editValues.labor_cost : row.labor_cost;
                    const final_profit = profit_amt - current_labor;

                    return (
                      <tr key={row.forecast_id} className="hover:bg-blue-50 group transition-colors">
                        <td className="border p-1.5 text-slate-400 font-mono">{idx + 1}</td>
                        <td className="border p-1.5">{row.month}월</td>
                        <td className="border p-1.5 text-left font-bold text-slate-700 px-3">{row.client_name}</td>
                        <td className="border p-1.5 text-slate-500">{row.team_name}</td>
                        <td className="border p-1.5 font-bold bg-slate-50/50">{row.manager_name}</td>
                        <td className="border p-1.5 text-right">{row.contract_product.toLocaleString()}</td>
                        <td className="border p-1.5 text-right">{row.contract_goods.toLocaleString()}</td>
                        <td className="border p-1.5 text-right font-bold text-blue-600 bg-slate-50">{contract_total.toLocaleString()}</td>
                        <td className="border p-1 text-[9px] text-slate-400">{contract_total ? (row.contract_product/contract_total*100).toFixed(0) : 0}%</td>
                        <td className="border p-1 text-[9px] text-slate-400">{contract_total ? (row.contract_goods/contract_total*100).toFixed(0) : 0}%</td>
                        <td className={`border p-0 ${isEditing ? 'bg-white' : 'bg-red-50/30'}`}>
                          {isEditing ? (
                            <input type="number" className="w-full h-full p-1.5 text-right border-2 border-blue-500 outline-none font-bold text-blue-600" value={editValues.purchase_cost} onChange={(e) => setEditValues({...editValues, purchase_cost: Number(e.target.value)})} />
                          ) : (
                            <span className="block p-1.5 text-right font-bold text-red-600">{row.purchase_cost.toLocaleString()}</span>
                          )}
                        </td>
                        <td className="border p-1.5 text-right bg-slate-50 font-medium">{profit_amt.toLocaleString()}</td>
                        <td className="border p-1.5 text-right font-bold text-blue-700">{profit_rate.toFixed(1)}%</td>
                        <td className={`border p-0 ${isEditing ? 'bg-white' : 'bg-red-50/30'}`}>
                          {isEditing ? (
                            <input type="number" className="w-full h-full p-1.5 text-right border-2 border-blue-500 outline-none font-bold text-blue-600" value={editValues.labor_cost} onChange={(e) => setEditValues({...editValues, labor_cost: Number(e.target.value)})} />
                          ) : (
                            <span className="block p-1.5 text-right font-bold text-red-600">{row.labor_cost.toLocaleString()}</span>
                          )}
                        </td>
                        <td className="border p-1.5 text-right font-black text-green-700 bg-green-50">{final_profit.toLocaleString()}</td>
                        <td className="border p-1.5">
                           {isEditing ? (
                             <div className="flex gap-1 justify-center">
                                <button onClick={saveEdit} className="text-green-600 hover:scale-110 transition"><Check size={18}/></button>
                                <button onClick={() => setEditingId(null)} className="text-red-600 hover:scale-110 transition"><X size={18}/></button>
                             </div>
                           ) : (
                             <Edit3 size={14} className="mx-auto text-slate-300 group-hover:text-blue-500 cursor-pointer" onClick={()=>startEdit(row)}/>
                           )}
                        </td>
                      </tr>
                    );
                  })}
                  <tr className="bg-amber-100/70 font-black text-[10px] border-b-2 border-slate-300">
                    <td colSpan={5} className="border border-slate-300 p-2 text-center text-amber-900 tracking-[0.2em]">{manager.toUpperCase()} SUMMARY</td>
                    <td className="border border-slate-300 p-2 text-right">{sub_product.toLocaleString()}</td>
                    <td className="border border-slate-300 p-2 text-right">{sub_goods.toLocaleString()}</td>
                    <td className="border border-slate-300 p-2 text-right text-blue-700 bg-amber-200/50">{sub_total_sales.toLocaleString()}</td>
                    <td colSpan={2} className="border border-slate-300 bg-slate-50/30"></td>
                    <td className="border border-slate-300 p-2 text-right text-red-600">{sub_purchase.toLocaleString()}</td>
                    <td className="border border-slate-300 p-2 text-right text-slate-800">{sub_profit_amt.toLocaleString()}</td>
                    <td className="border border-slate-300 p-2 text-right text-blue-700">{sub_profit_rate.toFixed(1)}%</td>
                    <td className="border border-slate-300 p-2 text-right text-red-600">{sub_labor.toLocaleString()}</td>
                    <td className="border border-slate-300 p-2 text-right text-green-800 bg-green-200/50">{sub_final_profit.toLocaleString()}</td>
                    <td className="border border-slate-300"></td>
                  </tr>
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}